import { IPaginate } from "@/types/app";
import {
  CountPCsTimeslotsQuery,
  CountPCsTimeslotsQueryVariables,
  GetPCsTimeslotsQuery,
  GetPCsTimeslotsQueryVariables,
  GetTimeslotsQuery,
  GetTimeslotsQueryVariables,
} from "@/types/gql/generated/graphql";
import { apolloClient } from "@/utils/graphql-client";
import {
  COUNT_PCsTimeslots,
  GET_PCsTimeslots,
  GET_Timeslots,
} from "./queries/timeslot.graphql";
import dayjs from "dayjs";
import {
  ITimeSlotSyncCondition,
  IUpdatedTimeslots,
} from "@/types/db/timeslots";
import { LogService } from "@/services/logging";

export async function getPCsTimeSlots(ids: number[], pagination?: IPaginate) {
  const variables: GetPCsTimeslotsQueryVariables = {
    categories: `(category_id,in,${ids.join(",")})`,
    condition: {
      Bookable: {
        in: ["1"],
      },
    },
  };

  if (pagination) {
    const { skip, limit } = pagination;
    variables.skip = skip;
    variables.limit = limit;
  }

  const res = await apolloClient.query<
    GetPCsTimeslotsQuery,
    GetPCsTimeslotsQueryVariables
  >({
    query: GET_PCsTimeslots,
    variables,
  });

  return res.data;
}

/**
 *
 * @param ids
 * @param periodStart
 * @param periodEnd
 * @param pagination
 * @returns an array of timeslots from remote db that end within a period of time
 */
export async function getPCsTimeSlotsByPeriod(
  ids: number[],
  periodStart: number,
  periodEnd: number,
  pagination?: IPaginate
) {
  // I tried with: EndDateTime: {ge: dayjs(periodStart).utc().format("YYYY-MM-DD HH:mm:ss"),}, after editing ConditionString but the server complained too in the response
  const variables: GetPCsTimeslotsQueryVariables = {
    categories: `(category_id,in,${ids.join(",")})~and(endDateTime,ge,${dayjs(
      periodStart
    )
      .utc()
      .format("YYYY-MM-DD HH:mm:ss")}))~and(startDateTime,le,${dayjs(periodEnd)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss")})`,
    condition: {
      Bookable: {
        in: ["1"],
      },
    },
  };

  if (pagination) {
    const { skip, limit } = pagination;
    variables.skip = skip;
    variables.limit = limit;
  }

  const res = await apolloClient.query<
    GetPCsTimeslotsQuery,
    GetPCsTimeslotsQueryVariables
  >({
    query: GET_PCsTimeslots,
    variables,
  });

  return res.data;
}

/**
 *
 * @param ids
 * @param periodStart
 * @param periodEnd
 * @param pagination
 * @returns an array of timeslots from remote db that start within a period of time
 */
export async function getPCsTimeSlotsByPeriodRelativeToStart(
  ids: number[],
  periodStart: number,
  periodEnd: number,
  pagination?: IPaginate
) {
  const variables: GetPCsTimeslotsQueryVariables = {
    categories: `(category_id,in,${ids.join(",")})~and(startDateTime,ge,${dayjs(
      periodStart
    )
      .utc()
      .format("YYYY-MM-DD HH:mm:ss")}))~and(startDateTime,le,${dayjs(periodEnd)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss")})`,
    condition: {
      Bookable: {
        in: ["1"],
      },
    },
  };
  if (pagination) {
    const { skip, limit } = pagination;
    variables.skip = skip;
    variables.limit = limit;
  }
  const res = await apolloClient.query<
    GetPCsTimeslotsQuery,
    GetPCsTimeslotsQueryVariables
  >({
    query: GET_PCsTimeslots,
    variables,
  });

  return res.data;
}

/**
 *
 * @param ids
 * @param date
 * @param pagination
 * @returns an array of timeslots from remote db that starts before a given a date
 */
export async function getPCsTimeSlotsBeforeDate(
  ids: number[],
  date: number,
  pagination?: IPaginate
) {
  // 'date' passed as a paramater is the first moment after the period we want timeslots for
  // 'midnight' is the first moment of the last day of the period.
  const midnight = dayjs(date)
    .add(-1, "day")
    .startOf("day")
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");

  const variables: GetPCsTimeslotsQueryVariables = {
    categories: `(category_id,in,${ids.join(",")})~and(startDateTime,le,${dayjs(
      date
    )
      .utc()
      .format("YYYY-MM-DD HH:mm:ss")})~and(endDateTime,ge,${midnight})`,
    condition: {
      Bookable: {
        in: ["1"],
      },
    },
  };

  if (pagination) {
    const { skip, limit } = pagination;
    variables.skip = skip;
    variables.limit = limit;
  }

  const res = await apolloClient.query<
    GetPCsTimeslotsQuery,
    GetPCsTimeslotsQueryVariables
  >({
    query: GET_PCsTimeslots,
    variables,
  });

  return res.data;
}

/**
 *
 * @param ids
 * @param date
 * @param pagination
 * @returns an array of timeslots from remote db that starts before a given a date
 */
export async function CountPCsTimeSlotsBeforeDate(ids: number[], date: number) {
  // 'date' passed as a paramater is the first moment after the period we want timeslots for
  // 'midnight' is the first moment of the last day of the period.
  const midnight = dayjs(date)
    .add(-1, "day")
    .startOf("day")
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");

  const variables: CountPCsTimeslotsQueryVariables = {
    categories: `(category_id,in,${ids.join(",")})~and(startDateTime,le,${dayjs(
      date
    )
      .utc()
      .format("YYYY-MM-DD HH:mm:ss")})~and(endDateTime,ge,${midnight})`,
    condition: {
      Bookable: {
        in: ["1"],
      },
    },
  };

  const res = await apolloClient.query<
    CountPCsTimeslotsQuery,
    CountPCsTimeslotsQueryVariables
  >({
    query: COUNT_PCsTimeslots,
    variables,
  });

  return res.data;
}

/**
 *
 * @param condition
 * Fetches synced timeslots from remote db
 */
export async function getSyncedTimeslots(
  syncCond: ITimeSlotSyncCondition
): Promise<IUpdatedTimeslots[]> {
  LogService.info(`Sending request to synch timeslots availabilities`, {
    syncCond,
  });
  const res = await apolloClient.query<
    GetTimeslotsQuery,
    GetTimeslotsQueryVariables
  >({
    query: GET_Timeslots,
    variables: {
      categories: `(category_id,in,${syncCond.ids.join(
        ","
      )})~and(${syncCond.timeRanges.join("~or")})`,
      condition: {
        // Bookable: {
        //   in: ["1"],
        // },
      },
    },
  });

  LogService.info(`Receiving response to synch timeslots availabilities`, {
    res,
  });
  return res.data.timeslots as IUpdatedTimeslots[];
}

/**
 * Pulls all timeslots for a given condition string
 * @param condition
 * @returns
 */
export async function getTimeslotsByCondition(
  condition: string,
  timeout = 1000
): Promise<IUpdatedTimeslots[]> {
  LogService.info(`Availability: Sending request to synch timeslots`, {
    params: {
      condition,
      timeout,
    },
  });

  const abortController = new AbortController();
  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, timeout);

  try {
    const res = await apolloClient.query<
      GetTimeslotsQuery,
      GetTimeslotsQueryVariables
    >({
      query: GET_Timeslots,
      variables: {
        categories: condition,
        condition: {},
      },
      context: {
        fetchOptions: {
          signal: abortController.signal,
        },
      },
      errorPolicy: "all",
      fetchPolicy: "network-only",
    });

    clearTimeout(timeoutId);

    return res.data.timeslots as IUpdatedTimeslots[];
  } catch (error) {
    clearTimeout(timeoutId);

    // handle abort/timeout errors
    if (abortController.signal.aborted) {
      LogService.info(
        `Availability: GetTimeslots query was aborted/timed out after ${timeout}ms`,
        {
          params: {
            condition,
          },
        }
      );
      throw new Error(`GetTimeslots query timed out after ${timeout}ms`);
    }

    // handle other errors
    LogService.info(`Availability: Failed to fetch timeslots`, {
      params: {
        condition,
      },
      error,
    });

    throw error;
  }
}
